import React, { createContext, useContext } from 'react';
import type { ElementNodeDto, ElementType, ThemeStylesDto } from '@xshop/shared';
import { ELEMENT_ATTRIBUTE, ELEMENT_TYPE_ATTRIBUTE, type ElementMarkAttrs } from '../../editor/editorElement';
import { useElementStyles } from '../style';
import type { StoreFacts } from './storeFacts';

// What the walker needs from outside itself, and nothing more. Three things arrive from elsewhere: the
// store's design tokens (which is all the style engine needs to write a node's CSS), the theme component
// behind a `preset`, and the renderers for the element types other folders own — commerce, interactive.
// Taking all three as wiring rather than as imports is what lets this folder draw a page without knowing
// that a theme exists.

/** One element's own component: the node it draws, and the children the walker already drew for it. */
export type ElementComponentProps = { node: ElementNodeDto; children?: React.ReactNode };

/** A drawing function for one element type. Every one of them renders with `props: {}` and `styles: {}`. */
export type ElementComponent = React.FC<ElementComponentProps>;

/**
 * Draws the theme's own component for a `section` carrying a `preset`. Returning nothing means the theme
 * has no such component, and the section draws its children instead — which is exactly what a merchant
 * sees the moment they take a preset apart.
 */
export type PresetRenderer = (node: ElementNodeDto) => React.ReactNode;

/**
 * Stamps one node with everything the canvas reads back off the page: its id, its type, what the catalog
 * lets a merchant drop inside it and the prop a double-click types into. Wired by the mount rather than
 * imported, because the last two are catalog answers and the catalog is builder data — a shopper's bundle
 * must not carry seventy definitions to spell an attribute nobody on that page has.
 */
export type ElementMarks = (node: ElementNodeDto) => ElementMarkAttrs;

/** Everything the walk carries down the tree. */
export type ElementRenderContextValue = {
  /** The store's tokens, so `{ token: 'colors.primary' }` resolves to what this shop is painted in. */
  theme: ThemeStylesDto;
  renderPreset: PresetRenderer | null;
  /** Renderers for the types this folder does not own, keyed by type; they win over the built-in ones. */
  renderers: Readonly<Partial<Record<ElementType, ElementComponent>>>;
  /** True only inside the builder. A shopper's page carries no mark, so nothing there can be selected. */
  editing: boolean;
  /** The editor's eye preview plays motion while commerce remains in editor mode. */
  motionPreview?: boolean;
  /** What the shop can answer a bound prop with; null where there is no shell, as in a bare preview. */
  store: StoreFacts | null;
  /** The catalog's own marks, when the mount has them; without one a node still wears its id and its type. */
  marks?: ElementMarks | null;
};

const ElementRenderContext = createContext<ElementRenderContextValue | null>(null);

/** Hands the wiring to every node under it, so no element has to be passed the store's tokens by hand. */
export const ElementRenderProvider = ElementRenderContext.Provider;

// Reads the wiring the tree was drawn with; an element outside a tree is a bug in a mount, not a page.
export function useElementRender(): ElementRenderContextValue {
  const value = useContext(ElementRenderContext);
  if (!value) throw new Error('An element was drawn outside an ElementTreeView');
  return value;
}

/** What every drawn element wears: its class, its own id, and in the builder the marks that select it. */
export type ElementBoxAttrs = { className?: string; id?: string; 'data-bound'?: string } & Partial<ElementMarkAttrs>;

/**
 * Resolves the class one node wears, and the builder mark a shopper's page is never given.
 *
 * The class comes from the style engine and nothing here computes CSS: `useElementStyles` compiles the
 * node's own styles per breakpoint and per state, writes `hiddenOn` as a `display:none` in each band the
 * merchant hid it at, and keeps the rule in the page's stylesheet for as long as the element is drawn.
 * One render covers all three widths, so nothing re-renders when the window is resized.
 */
export function useElementBox(node: ElementNodeDto): ElementBoxAttrs {
  const { theme, editing, marks } = useElementRender();
  const compiled = useElementStyles({ theme, styles: node.styles, hiddenOn: node.hiddenOn });
  // The merchant's own class rides after the compiled one, so their stylesheet wins the specificity tie.
  const className = [compiled, node.cssClass].filter(Boolean).join(' ') || undefined;
  // What a bound node reads from, so a theme option can switch a card's part off by what it shows.
  const own = { ...(node.domId ? { id: node.domId } : {}), ...(node.bindings ? { 'data-bound': Object.values(node.bindings).join(' ') } : {}) };
  if (!editing) return { className, ...own };
  return {
    className,
    ...own,
    ...(marks ? marks(node) : { [ELEMENT_ATTRIBUTE]: node.id, [ELEMENT_TYPE_ATTRIBUTE]: node.type })
  };
}
